// src/pages/Utilisateurs/PrepaPhysique/components/PrepaPhysiqueExerciseCard.jsx

import React from "react";
import {
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaEye,
  FaImage,
  FaYoutube,
} from "react-icons/fa";

import { FaRepeat } from "react-icons/fa6";

import {
  formatDateFR,
  getExerciseDone,
  getExerciseProgramme,
  getExerciseRealisationDate,
  getExerciseRepetitions,
  toAbsoluteMediaUrl,
  truncateText,
} from "../utils/prepaPhysiqueUtils";

import DoneSwitch from "./DoneSwitch";

const FALLBACK_IMG = "/img/no-image.jpg";

export default function PrepaPhysiqueExerciseCard({
  exercise,
  saving,
  onOpen,
  onToggle,
}) {
  const done = getExerciseDone(exercise);

  const imageUrl = toAbsoluteMediaUrl(exercise?.lien_photo) || FALLBACK_IMG;
  const videoUrl = exercise?.lien_video || "";
  const programme = getExerciseProgramme(exercise);
  const repetitions = getExerciseRepetitions(exercise);
  const dateRealisation = getExerciseRealisationDate(exercise);
  const descriptionPreview = truncateText(exercise?.description, 120);

  return (
    <article className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-950">
      <button
        type="button"
        onClick={onOpen}
        className="relative block w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-900"
        aria-label={`Ouvrir ${exercise?.nom || "exercice"}`}
      >
        <div className="aspect-[16/10] w-full">
          <img
            src={imageUrl}
            alt={exercise?.nom || "Illustration exercice"}
            className="h-full w-full object-cover transition duration-500 hover:scale-[1.04]"
            loading="lazy"
            onError={(event) => {
              event.currentTarget.src = FALLBACK_IMG;
            }}
          />
        </div>

        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {programme && (
            <span className="inline-flex rounded-full bg-slate-950/80 px-3 py-1 text-[11px] font-bold uppercase text-white backdrop-blur">
              {programme}
            </span>
          )}
        </div>

        <div className="absolute right-3 top-3">
          <span
            className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase shadow-sm ${
              done
                ? "bg-emerald-500 text-white"
                : "bg-white/90 text-slate-700 backdrop-blur"
            }`}
          >
            {done ? "Réalisé" : "À faire"}
          </span>
        </div>
      </button>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h4 className="line-clamp-2 text-base font-bold leading-snug text-slate-950 dark:text-white">
              {exercise?.nom || "Exercice"}
            </h4>

            {descriptionPreview && (
              <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {descriptionPreview}
              </p>
            )}
          </div>

          <DoneSwitch
            checked={done}
            disabled={saving}
            onChange={onToggle}
            title={done ? "Basculer en non réalisé" : "Marquer comme réalisé"}
          />
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <MetaPill icon={FaRepeat} label={`Rép. ${repetitions}`} />

          {dateRealisation && dateRealisation !== "0000-00-00" && (
            <MetaPill
              icon={FaCalendarAlt}
              label={formatDateFR(dateRealisation)}
            />
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
          >
            <FaEye />
            Détails
          </button>

          {videoUrl ? (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 text-sm font-bold text-red-700 transition active:scale-95 dark:bg-red-950/30 dark:text-red-300"
            >
              <FaYoutube />
              Vidéo
              <FaExternalLinkAlt className="text-[10px]" />
            </a>
          ) : (
            <span className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 text-sm font-bold text-slate-400 dark:bg-slate-800">
              <FaImage />
              Support
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

function MetaPill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
      <Icon className="text-[11px]" />
      {label}
    </span>
  );
}